import { tradingClient } from "@/shared/rpc/clients";
import type { TPagination } from "@/shared/types";
import { OPEN_BET_LIFECYCLES } from "@/shared/types";
import { TFilters } from "@/shared/ui/filters/types";
import { parseEnumListFilter } from "@/utils/parse-enum-list-filter";
import { AccountType } from "@cronika/proto/common/billing_pb";
import { SortDirection } from "@cronika/proto/common/common_pb";
import type { BetMessage } from "@cronika/proto/common/trading_pb";
import { BetLifecycle } from "@cronika/proto/common/trading_pb";
import { makeAutoObservable, runInAction } from "mobx";

const DEFAULT_SIZE = 25;

const DEFAULT_REQUEST_PARAMS = {
  sortBy: "createdAt",
  sortDir: SortDirection.DESC,
  lifecycle: [BetLifecycle.SETTLED],
} as const;

const INITIAL_PAGINATION: TPagination = {
  page: 1,
  size: 0,
  total: 0,
};

// AccountTypes included when showing "Real" tab: real + bonus + partner
const REAL_TAB_ACCOUNT_TYPES: AccountType[] = [
  AccountType.REAL,
  AccountType.BONUS,
  AccountType.PARTNER,
];

const accountTypeMap: Record<string, AccountType> = {
  demo: AccountType.DEMO,
  real: AccountType.REAL,
  bonus: AccountType.BONUS,
  partner: AccountType.PARTNER,
  [String(AccountType.DEMO)]: AccountType.DEMO,
  [String(AccountType.REAL)]: AccountType.REAL,
  [String(AccountType.BONUS)]: AccountType.BONUS,
  [String(AccountType.PARTNER)]: AccountType.PARTNER,
};

class HistoryStore {
  list: BetMessage[] = [];
  activeBets: BetMessage[] = [];
  bonusClosedList: BetMessage[] = [];
  pagination: TPagination = INITIAL_PAGINATION;
  fetching = true;
  size = DEFAULT_SIZE;
  filters: TFilters = {};
  _fetchTimer: ReturnType<typeof setTimeout> | null = null;
  _fetchGen = 0;

  constructor() {
    makeAutoObservable(this, { _fetchTimer: false, _fetchGen: false });
  }

  get hasMore() {
    return this.list.length < this.pagination.total;
  }

  setFilters(filters: TFilters) {
    this.filters = filters;
  }

  setSize(size: number) {
    this.size = size;
  }

  fetch() {
    if (this._fetchTimer) clearTimeout(this._fetchTimer);
    this._fetchTimer = setTimeout(() => {
      this._fetchTimer = null;
      this.fetchList();
      // Skip separate active-bets fetch when lifecycle filter is already "open"
      // because fetchList already covers those bets in that case
      if (this.filters.lifecycle !== "open") {
        this.fetchActiveBets(this.filters.accountType);
      }
    }, 50);
  }

  async fetchList() {
    const gen = ++this._fetchGen;
    try {
      runInAction(() => {
        this.fetching = true;
      });

      const { filters, size } = this;
      const page = filters.page ? Number(filters.page) : 1;

      const { page: _page, ...restFilters } = filters;
      const cleanFilters: Record<string, any> = {
        ...restFilters,
        ...(filters.createdAtFrom && {
          createdAtFrom: new Date(filters.createdAtFrom).toISOString(),
        }),
        ...(filters.settledAtTo && {
          settledAtTo: new Date(filters.settledAtTo).toISOString(),
        }),
      };

      // Resolve lifecycle: "open" → OPEN_BET_LIFECYCLES, string enum → array
      let lifecycle: BetLifecycle[] = [...DEFAULT_REQUEST_PARAMS.lifecycle];
      if (cleanFilters.lifecycle === "open") {
        lifecycle = [...OPEN_BET_LIFECYCLES];
      } else if (cleanFilters.lifecycle != null) {
        lifecycle = Array.isArray(cleanFilters.lifecycle)
          ? cleanFilters.lifecycle
          : [cleanFilters.lifecycle];
      }
      delete cleanFilters.lifecycle;

      // Resolve accountType string → proto enum
      const accountTypeStr = cleanFilters.accountType as string | undefined;
      const resolvedAccountType = accountTypeStr
        ? accountTypeMap[accountTypeStr] ?? undefined
        : undefined;
      delete cleanFilters.accountType;

      const isDemo = resolvedAccountType === AccountType.DEMO;

      // Resolve betType filter → number array
      const betType = parseEnumListFilter(cleanFilters.betType);

      // Resolve outcome filter → number array
      const outcome = parseEnumListFilter(cleanFilters.outcome);

      // For non-demo tabs: query real+bonus+partner in a single request via accountTypes[].
      // For demo tab: query only demo via single accountType.
      const accountFilter = isDemo
        ? { accountType: AccountType.DEMO }
        : { accountTypes: REAL_TAB_ACCOUNT_TYPES };

      const request = {
        sortBy: DEFAULT_REQUEST_PARAMS.sortBy,
        sortDir: DEFAULT_REQUEST_PARAMS.sortDir,
        lifecycle,
        ...accountFilter,
        ...(cleanFilters.pairId && { pairId: cleanFilters.pairId }),
        ...(cleanFilters.createdAtFrom && { createdAtFrom: cleanFilters.createdAtFrom }),
        ...(cleanFilters.settledAtTo && { settledAtTo: cleanFilters.settledAtTo }),
        ...(betType?.length && { betType }),
        ...(outcome?.length && { outcome }),
        pagination: { page, size },
      };

      const response = await tradingClient.listBets(request);

      if (gen !== this._fetchGen) return;

      runInAction(() => {
        this.list = response.data ?? [];
        const pg = response.pagination;
        this.pagination = {
          page: pg?.page ?? page,
          size: pg?.size ?? size,
          total: pg?.total ?? 0,
        };
      });
    } catch {
      // silent
    } finally {
      if (gen === this._fetchGen) {
        runInAction(() => {
          this.fetching = false;
        });
      }
    }
  }

  async fetchActiveBets(accountType?: string) {
    try {
      const resolvedAccountType = accountType
        ? accountTypeMap[accountType] ?? undefined
        : undefined;

      const isDemo = resolvedAccountType === AccountType.DEMO;

      const accountFilter = isDemo
        ? { accountType: AccountType.DEMO }
        : { accountTypes: REAL_TAB_ACCOUNT_TYPES };

      const response = await tradingClient.listBets({
        pagination: { page: 1, size: 100 },
        sortBy: "createdAt",
        sortDir: SortDirection.DESC,
        lifecycle: [...OPEN_BET_LIFECYCLES],
        ...accountFilter,
      });

      runInAction(() => {
        this.activeBets = response.data ?? [];
      });
    } catch {
      // silent
    }
  }

  async fetchBonusClosedBets(currentAccountType: "real" | "demo") {
    if (currentAccountType !== "real") {
      runInAction(() => {
        this.bonusClosedList = [];
      });
      return;
    }
    try {
      const response = await tradingClient.listBets({
        accountTypes: [AccountType.BONUS],
        lifecycle: [BetLifecycle.SETTLED],
        pagination: { size: 50 },
        sortBy: "settledAt",
        sortDir: SortDirection.DESC,
      });
      runInAction(() => {
        this.bonusClosedList = response.data ?? [];
      });
    } catch {
      // silent
    }
  }

  prependItem(item: BetMessage) {
    if (this.list.some((b) => b.id === item.id)) return;
    this.list.unshift(item);
    this.pagination.total += 1;
  }

  removeItem(id: string) {
    this.list = this.list.filter((b) => b.id !== id);
    this.pagination.total = Math.max(0, this.pagination.total - 1);
  }
}

export const historyStore = new HistoryStore();
